import React, { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { IoCopyOutline, IoEllipsisVertical } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';

import { Table } from 'src/components/TableNew';
import NoResult from 'src/components/NoResult/NoResult';
import { FilterBar, type FilterDef } from 'src/components/FilterBar';
import { ConfirmModal } from 'src/components/ConfirmModal';
import { PermissionGuard } from 'src/components/PermissionGuard';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { PERMISSIONS } from 'src/utils/permissions';
import {
  WhitelistEntry,
  fetchComplianceWhitelist,
  forceWhitelistRescreen,
  formatDueIn,
  formatNetworkLabel,
  formatWalletOwnerName,
} from 'src/api/adminWallets';
import { convertDate, shortenString } from 'src/components/Table/helpers';
import { useFilterQueryEffect } from 'src/hooks/useFilterQueryEffect';
import { ROUTE_USERS } from 'src/utils/routes';

import {
  ActionsRow,
  CopyButton,
  DetailValue,
  DueBadge,
  RescreenButton,
  UserLink,
} from './styled';

const CHAIN_OPTIONS = [
  { value: 'ethereum', label: 'Ethereum' },
  { value: 'bitcoin', label: 'Bitcoin' },
  { value: 'tron', label: 'Tron' },
  { value: 'polygon', label: 'Polygon' },
  { value: 'solana', label: 'Solana' },
  { value: 'litecoin', label: 'Litecoin' },
];

const TYPE_OPTIONS = [
  { value: 'DEPOSIT', label: 'Deposit' },
  { value: 'WITHDRAWAL', label: 'Withdrawal' },
];

// One bar per filter; the API ANDs them (`?search=&chain=&type=&status=`).
const WHITELIST_FILTERS: FilterDef[] = [
  { key: 'search', label: 'Address', placeholder: 'Search address…' },
  { key: 'chain', label: 'Chain', options: CHAIN_OPTIONS, allLabel: 'All chains' },
  { key: 'type', label: 'Type', options: TYPE_OPTIONS, allLabel: 'All types' },
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'ACTIVE', label: 'Active' },
      { value: 'DUE', label: 'Screening due' },
      { value: 'REVOKED', label: 'Revoked' },
    ],
    allLabel: 'All statuses',
  },
];

const HEADERS = [
  'User',
  'Address',
  'CCY',
  'First txn',
  'Txn count',
  'Last txn',
  'Last screened',
  'Next screening',
];

type WhitelistTabProps = {
  onTotalChange?: Dispatch<SetStateAction<number | null>>;
};

const WhitelistTab: React.FC<WhitelistTabProps> = ({ onTotalChange }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<WhitelistEntry[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [rescreenTarget, setRescreenTarget] = useState<WhitelistEntry | null>(null);
  const [rescreening, setRescreening] = useState(false);

  const load = useCallback(
    async (params: { page: number; filters: Record<string, string> }) => {
      setLoading(true);
      try {
        const res = await fetchComplianceWhitelist({
          page: params.page,
          limit: 10,
          sort: 'whitelisted_at',
          order: 'desc',
          chain: params.filters.chain || undefined,
          type: (params.filters.type as 'DEPOSIT' | 'WITHDRAWAL') || undefined,
          status: (params.filters.status as 'ACTIVE' | 'DUE' | 'REVOKED') || undefined,
          search: params.filters.search || undefined,
        });
        setEntries(res.whitelist);
        setTotalPages(Math.max(1, res.meta.page_count || 1));
        onTotalChange?.(res.meta.total_count || 0);
      } catch (e: any) {
        dispatch(
          setAppAlert({
            message: e?.response?.data?.error ?? 'Failed to load whitelist',
            isSuccess: false,
          }),
        );
        setEntries([]);
        onTotalChange?.(0);
      } finally {
        setLoading(false);
      }
    },
    [dispatch, onTotalChange],
  );

  const { applied: appliedFilters, refresh: refreshQuery } = useFilterQueryEffect({ filters, currentPage, setCurrentPage, query: load });

  // Actions below (rescreen) refetch the current page with the current filters.
  const reload = useCallback(
    () => load({ page: currentPage, filters: appliedFilters }),
    [appliedFilters, currentPage, load],
  );

  const rows = useMemo(
    () =>
      entries.map((entry) => ({
        ...entry,
        user_name: formatWalletOwnerName(entry.user),
        address_display: entry.address,
        ccy: formatNetworkLabel(entry.network),
        first_txn: entry.first_used_at,
        txn_count: entry.use_count ?? 0,
        last_txn: entry.last_used_at,
        last_screened: entry.last_screened_at,
        next_screening: formatDueIn(entry.due_in_ms, entry.status).label,
        next_due: formatDueIn(entry.due_in_ms, entry.status).due,
      })),
    [entries],
  );

  const handleRescreen = async () => {
    if (!rescreenTarget?.id) return;
    setRescreening(true);
    try {
      const result = await forceWhitelistRescreen(rescreenTarget.id);
      const messages: Record<string, string> = {
        RENEWED: 'Address re-screened — validity window renewed.',
        REVOKED: 'Address revoked after re-screening.',
        FAILED: 'Re-screen failed — try again later.',
      };
      dispatch(
        setAppAlert({
          message: messages[result.outcome] || 'Re-screen completed.',
          isSuccess: result.outcome === 'RENEWED',
        }),
      );
      setRescreenTarget(null);
      reload();
    } catch (e: any) {
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? 'Failed to force re-screen',
          isSuccess: false,
        }),
      );
    } finally {
      setRescreening(false);
    }
  };

  const copyAddress = async (address?: string | null) => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      dispatch(setAppAlert({ message: 'Address copied', isSuccess: true }));
    } catch {
      dispatch(setAppAlert({ message: 'Failed to copy', isSuccess: false }));
    }
  };

  return (
    <>
      <FilterBar
        filters={WHITELIST_FILTERS}
        values={filters}
        onChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
        onClear={() => setFilters({})}
        onRefresh={refreshQuery}
      />

      {rows.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={HEADERS}
          items={rows}
          columns="0.9fr 1fr 0.5fr 0.8fr 0.6fr 0.8fr 0.9fr 0.9fr"
          fieldsToShow={[
            'user_name',
            'address_display',
            'ccy',
            'first_txn',
            'txn_count',
            'last_txn',
            'last_screened',
            'next_screening',
          ]}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          disableRowDetails
          actionsHeader="Actions"
          renderCell={(item, field) => {
            if (field === 'user_name') {
              const value = String(item.user_name || '—');
              const userId = item.user?.id;
              const email = item.user?.email;

              if (!userId) {
                return <span title={email ? `${value} (${email})` : value}>{value}</span>;
              }

              return (
                <UserLink
                  type="button"
                  title={email ? `${value} (${email})` : value}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`${ROUTE_USERS}/${userId}`);
                  }}
                >
                  {value}
                </UserLink>
              );
            }
            if (field === 'address_display') {
              const value = String(item.address_display || '');
              if (!value) return <span>—</span>;
              return (
                <DetailValue>
                  <span title={value}>{shortenString(value)}</span>
                  <CopyButton
                    type="button"
                    aria-label="Copy address"
                    title="Copy address"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyAddress(value);
                    }}
                  >
                    <IoCopyOutline size={16} />
                  </CopyButton>
                </DetailValue>
              );
            }
            if (field === 'first_txn' || field === 'last_txn' || field === 'last_screened') {
              const value = item[field];
              return <span>{value ? convertDate(String(value)).date : '—'}</span>;
            }
            if (field === 'next_screening') {
              return (
                <DueBadge $due={Boolean(item.next_due)}>
                  {String(item.next_screening || '—')}
                </DueBadge>
              );
            }
            return null;
          }}
          actions={(item) => (
            <PermissionGuard permission={PERMISSIONS.TRANSACTION_WRITE}>
              <ActionsRow>
                <RescreenButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRescreenTarget(item as WhitelistEntry);
                  }}
                >
                  Force rescreen
                </RescreenButton>
                <RescreenButton type="button" aria-label="More actions" disabled>
                  <IoEllipsisVertical size={16} />
                </RescreenButton>
              </ActionsRow>
            </PermissionGuard>
          )}
        />
      ) : (
        <NoResult />
      )}

      {rescreenTarget ? (
        <ConfirmModal
          title="Force rescreen"
          message={
            <>
              Run an immediate Chainalysis re-screen for{' '}
              <strong>{shortenString(rescreenTarget.address)}</strong>?
            </>
          }
          confirmText="Force rescreen"
          onClose={() => setRescreenTarget(null)}
          onConfirm={handleRescreen}
          isLoading={rescreening}
        />
      ) : null}
    </>
  );
};

export default WhitelistTab;
